import { useEffect, useState } from "react";
import { listEscrows, listRates, listDrops, type EscrowRecord, type EscrowRate } from "../api/nftdropsApi";
import { hasContractDeployed, isMarketplaceRegistered, getMarketplaceBalance } from "../api/atomicApi";
import { registerMarketplace, withdrawMarketplaceBalance } from "../api/actions";
import { useWallet } from "../wallet/WalletContext";
import { adminAccounts, chain, contracts, marketplaceName } from "../config";
import type { NftDrop } from "../types";
import type { ActivityEvent } from "../api/activityLog";
import { ForumAdminPanel } from "../components/ForumAdminPanel";
import { EscrowUpgradePanel } from "../components/EscrowUpgradePanel";
import { ExchangeAdminPanel } from "../components/ExchangeAdminPanel";
import { lazy, Suspense } from "react";
const PolygonAdminPanel = lazy(() => import("../components/PolygonAdminPanel").then((m) => ({ default: m.PolygonAdminPanel })));
import { showError } from "../components/Dialog";

interface LoggedEvent extends ActivityEvent {
  key: string;
  receivedAt: string;
}

interface LoggedConnection {
  key: string;
  account: string;
  via: "explicit" | "restored";
  connectedAt: string;
}

const ACTIVITY_LABELS: Record<string, string> = {
  mint_plain: "Minted NFT",
  mint_backed_create: "Created backed NFT",
  mint_backed_deposit: "Deposited & minted",
  mint_drop: "Minted from drop",
  drop_created: "Created drop",
  redeem: "Redeemed (burned)",
  list_for_sale: "Listed for sale",
  buy: "Bought",
  collection_created: "Created collection (Bulk Create)",
  schemas_created: "Created schemas (Bulk Create)",
  templates_created: "Created templates (Bulk Create)",
  mint_polygon: "Minted on Polygon",
};

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/** Sums each distinct token symbol found in a list of "N.NNNNNNNN SYM"
 * quantity strings — used for "total value currently locked in escrow". */
function sumBySymbol(quantities: string[]): Record<string, number> {
  const totals: Record<string, number> = {};
  for (const q of quantities) {
    const [amount, symbol] = q.split(" ");
    if (!symbol) continue;
    totals[symbol] = (totals[symbol] ?? 0) + Number(amount || 0);
  }
  return totals;
}

/**
 * Admin dashboard: everything happening on WAXCreator in one place. Three
 * data sources feed it, and it's worth knowing which is which:
 *
 * - escrowbacker/nftdrops chain tables (rates, escrows, drops) — always
 *   accurate, read directly from the chain, can't be spoofed.
 * - the activity feed (Netlify Blobs, via log-activity/list-activity) —
 *   a best-effort convenience log the frontend writes to right after a
 *   signed transaction goes through, so you can see plain (unbacked)
 *   mints, sales, and listings too, which have no on-chain fingerprint
 *   tying them to this site specifically. It has no server-side
 *   verification against the chain, so treat it as "what the site saw",
 *   not as a financial record.
 * - the connections feed (Netlify Blobs, via log-connection/list-connections)
 *   — same best-effort nature, but logs *every account that's shown up with
 *   a wallet session* (connected or returned), not just ones that completed
 *   a transaction. This is what answers "is anyone actually using this
 *   site" independent of whether they went on to mint anything. Only
 *   captures activity from when this feature was added (2026-09-13) onward.
 *
 * Gated by connected wallet against `adminAccounts` (src/config.ts) —
 * client-side only, but the underlying data is either public chain state
 * or this best-effort log, never anything sensitive.
 */
export function Admin() {
  const { session, account, connect, connecting } = useWallet();
  const isAdmin = !!account && adminAccounts.includes(account);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [escrows, setEscrows] = useState<EscrowRecord[]>([]);
  const [rates, setRates] = useState<EscrowRate[]>([]);
  const [drops, setDrops] = useState<NftDrop[]>([]);
  const [events, setEvents] = useState<LoggedEvent[]>([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [connections, setConnections] = useState<LoggedConnection[]>([]);
  const [connectionsTotal, setConnectionsTotal] = useState(0);
  const [escrowDeployed, setEscrowDeployed] = useState<boolean | null>(null);
  const [dropsDeployed, setDropsDeployed] = useState<boolean | null>(null);
  const [marketRegistered, setMarketRegistered] = useState<boolean | null>(null);
  const [marketBalance, setMarketBalance] = useState<string[]>([]);
  const [marketBusy, setMarketBusy] = useState(false);

  async function loadAll() {
    setLoading(true);
    setError(null);
    try {
      const [
        escrowRows,
        rateRows,
        dropRows,
        activityRes,
        connectionsRes,
        escrowLive,
        dropsLive,
        marketReg,
        marketBal,
      ] = await Promise.all([
        listEscrows().catch(() => []),
        listRates().catch(() => []),
        listDrops().catch(() => []),
        fetch("/.netlify/functions/list-activity?limit=100")
          .then((r) => (r.ok ? r.json() : { events: [], total: 0 }))
          .catch(() => ({ events: [], total: 0 })),
        fetch("/.netlify/functions/list-connections?limit=200")
          .then((r) => (r.ok ? r.json() : { connections: [], total: 0 }))
          .catch(() => ({ connections: [], total: 0 })),
        hasContractDeployed(chain.url, contracts.escrow),
        hasContractDeployed(chain.url, contracts.nftdrops),
        marketplaceName ? isMarketplaceRegistered(chain.url, marketplaceName) : Promise.resolve(false),
        marketplaceName ? getMarketplaceBalance(chain.url, marketplaceName).catch(() => []) : Promise.resolve([]),
      ]);
      setEscrows(escrowRows);
      setRates(rateRows);
      setDrops(dropRows);
      setEvents(activityRes.events ?? []);
      setEventsTotal(activityRes.total ?? (activityRes.events ?? []).length);
      setConnections(connectionsRes.connections ?? []);
      setConnectionsTotal(connectionsRes.total ?? (connectionsRes.connections ?? []).length);
      setEscrowDeployed(escrowLive);
      setDropsDeployed(dropsLive);
      setMarketRegistered(marketReg);
      setMarketBalance(marketBal);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load admin data");
    } finally {
      setLoading(false);
    }
  }

  async function handleRegisterMarketplace() {
    if (!session || !marketplaceName) return;
    setMarketBusy(true);
    try {
      await registerMarketplace(session, marketplaceName);
      setMarketRegistered(true);
    } catch (err) {
      showError(err, "Registration failed");
    } finally {
      setMarketBusy(false);
    }
  }

  async function handleClaim(quantity: string) {
    if (!session) return;
    setMarketBusy(true);
    try {
      await withdrawMarketplaceBalance(session, quantity);
      setMarketBalance((prev) => prev.filter((q) => q !== quantity));
    } catch (err) {
      showError(err, "Claim failed");
    } finally {
      setMarketBusy(false);
    }
  }

  useEffect(() => {
    if (isAdmin) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (!session) {
    return (
      <div className="page page--narrow">
        <div className="page__header">
          <h1>Admin</h1>
          <p className="page__subtitle">Connect the wallet that manages WAXCreator to view this page.</p>
        </div>
        <button className="btn btn--primary" onClick={connect} disabled={connecting}>
          {connecting ? "Connecting…" : "Connect wallet"}
        </button>
      </div>
    );
  }

  // The exchange contract account can't be a normal site admin, but it's the
  // only account that can install/initialise the exchange contract (setcode
  // must be signed by the account itself). Give it just that panel.
  if (!isAdmin && account === contracts.orderbook) {
    return (
      <div className="page">
        <div className="page__header">
          <h1>Admin — exchange setup</h1>
          <p className="page__subtitle">
            You're connected as the exchange contract account <code>{account}</code>. Install and initialise the
            exchange here, then reconnect as a site admin to manage fees and markets.
          </p>
        </div>
        <ExchangeAdminPanel />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="page page--narrow">
        <div className="page__header">
          <h1>Admin</h1>
        </div>
        <div className="state-message state-message--error">
          {account} isn't an admin account for this site.
        </div>
      </div>
    );
  }

  const lockedTotals = sumBySymbol(escrows.map((e) => e.backing.quantity));
  const activeDrops = drops.filter((d) => d.enabled).length;
  const mintEvents = events.filter((e) => e.type.startsWith("mint")).length;
  const distinctWallets = new Set(connections.map((c) => c.account)).size;

  return (
    <div className="page">
      <div className="admin-refresh">
        <div className="page__header" style={{ marginBottom: 0 }}>
          <h1>Admin</h1>
          <p className="page__subtitle">
            NFTs, drops, escrow state, and recent activity across WAXCreator. Signed in as {account}.
          </p>
        </div>
        <button className="btn btn--outline" onClick={loadAll} disabled={loading}>
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && <div className="state-message state-message--error">{error}</div>}

      {(escrowDeployed === false || dropsDeployed === false) && (
        <div className="callout callout--warning" style={{ marginBottom: 20 }}>
          {escrowDeployed === false && (
            <div>
              ⚠️ escrowbacker (<code>{contracts.escrow}</code>) has no contract deployed on it yet.
            </div>
          )}
          {dropsDeployed === false && (
            <div>
              ⚠️ nftdrops (<code>{contracts.nftdrops}</code>) has no contract deployed on it yet.
            </div>
          )}
        </div>
      )}

      <EscrowUpgradePanel />

      <ExchangeAdminPanel />

      <Suspense fallback={null}>
        <PolygonAdminPanel />
      </Suspense>

      <div className="admin-stats">
        <div className="admin-stat">
          <div className="admin-stat__label">Backed NFTs outstanding</div>
          <div className="admin-stat__value">{escrows.length}</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat__label">Value locked in escrow</div>
          <div className="admin-stat__value">
            {Object.keys(lockedTotals).length === 0
              ? "—"
              : Object.entries(lockedTotals)
                  .map(([sym, amt]) => `${amt.toFixed(2)} ${sym}`)
                  .join(", ")}
          </div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat__label">Registered backing rates</div>
          <div className="admin-stat__value">{rates.length}</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat__label">Drops (active / total)</div>
          <div className="admin-stat__value">
            {activeDrops} / {drops.length}
          </div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat__label">Logged mints</div>
          <div className="admin-stat__value">{mintEvents}</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat__label">Logged activity (total)</div>
          <div className="admin-stat__value">{eventsTotal}</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat__label">Distinct wallets (recent)</div>
          <div className="admin-stat__value">{distinctWallets}</div>
        </div>
        <div className="admin-stat">
          <div className="admin-stat__label">Wallet connections logged (total)</div>
          <div className="admin-stat__value">{connectionsTotal}</div>
        </div>
      </div>

      <div className="admin-section">
        <h2>Marketplace commission</h2>
        <p className="admin-section__subtitle">
          Every listing and purchase this site sends to AtomicMarket (see <code>src/api/actions.ts</code>) already
          tags itself with <code>VITE_MARKETPLACE_NAME</code> as the maker/taker marketplace — AtomicMarket pays a
          1% cut of each sale to whichever marketplace facilitated the listing, and another 1% to whichever
          facilitated the purchase, on top of the collection's own royalty. That money doesn't land in your wallet
          automatically — it accumulates in AtomicMarket's own balance table until you claim it below.
        </p>
        {!marketplaceName && (
          <div className="callout callout--warning">
            <code>VITE_MARKETPLACE_NAME</code> isn't set — nothing this site does gets attributed to a marketplace
            yet. Set it (in Netlify's env vars) to a name you control, most simply your own account name (e.g.
            "{account}"), then register it below with that same wallet.
          </div>
        )}
        {marketplaceName && (
          <div className="admin-stats">
            <div className="admin-stat">
              <div className="admin-stat__label">Marketplace name</div>
              <div className="admin-stat__value">{marketplaceName}</div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat__label">Registration</div>
              <div className="admin-stat__value">
                {marketRegistered === null ? "—" : marketRegistered ? "Registered" : "Not registered"}
              </div>
            </div>
            <div className="admin-stat">
              <div className="admin-stat__label">Claimable balance</div>
              <div className="admin-stat__value">{marketBalance.length ? marketBalance.join(", ") : "—"}</div>
            </div>
          </div>
        )}
        {marketplaceName && marketRegistered === false && (
          <button className="btn btn--primary" onClick={handleRegisterMarketplace} disabled={marketBusy}>
            {marketBusy ? "Registering…" : `Register "${marketplaceName}" as a marketplace`}
          </button>
        )}
        {marketplaceName && marketRegistered && marketBalance.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Balance</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {marketBalance.map((q) => (
                  <tr key={q}>
                    <td>{q}</td>
                    <td>
                      <button className="btn btn--small" onClick={() => handleClaim(q)} disabled={marketBusy}>
                        {marketBusy ? "Claiming…" : "Claim to wallet"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="admin-section">
        <h2>Recent activity</h2>
        <p className="admin-section__subtitle">
          Best-effort log written by the site right after each signed transaction — see the note in this page's
          source for why this isn't a chain-verified record. Newest first, up to 100 shown.
        </p>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Account</th>
                <th>Collection</th>
                <th>Template / Drop / Asset</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr>
                  <td colSpan={6}>No activity logged yet.</td>
                </tr>
              )}
              {events.map((e) => (
                <tr key={e.key}>
                  <td>{formatTime(e.receivedAt)}</td>
                  <td>
                    <span className="admin-badge">{ACTIVITY_LABELS[e.type] ?? e.type}</span>
                  </td>
                  <td>{e.account}</td>
                  <td>{e.collectionName ?? "—"}</td>
                  <td>{e.templateId ?? e.dropId ?? e.assetId ?? "—"}</td>
                  <td>{e.amount ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-section">
        <h2>Connected wallets</h2>
        <p className="admin-section__subtitle">
          Every account that's established a wallet session on the site going forward — both an explicit "Connect
          wallet" click and a silently-restored return visit count, deduped to once per account per day per browser.
          This started logging 2026-09-13 onward — nothing before that date is captured. Newest first, up to 200
          shown ({connectionsTotal} total logged).
        </p>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Account</th>
                <th>How</th>
              </tr>
            </thead>
            <tbody>
              {connections.length === 0 && (
                <tr>
                  <td colSpan={3}>No wallet connections logged yet.</td>
                </tr>
              )}
              {connections.map((c) => (
                <tr key={c.key}>
                  <td>{formatTime(c.connectedAt)}</td>
                  <td>{c.account}</td>
                  <td>{c.via === "explicit" ? "Connected wallet" : "Return visit"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-section">
        <h2>Backed NFTs (live escrow)</h2>
        <p className="admin-section__subtitle">Read directly from escrowbacker's own table — always accurate.</p>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Asset</th>
                <th>Backing</th>
                <th>Minted at</th>
              </tr>
            </thead>
            <tbody>
              {escrows.length === 0 && (
                <tr>
                  <td colSpan={3}>No backed NFTs outstanding.</td>
                </tr>
              )}
              {escrows.map((e) => (
                <tr key={e.asset_id}>
                  <td>
                    <a href={`/asset/${e.asset_id}`}>{e.asset_id}</a>
                  </td>
                  <td>{e.backing.quantity}</td>
                  <td>{formatTime(e.minted_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-section">
        <h2>Backing rates</h2>
        <p className="admin-section__subtitle">Every template registered as backable, enabled or not.</p>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Template</th>
                <th>Collection</th>
                <th>Schema</th>
                <th>Backing</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rates.length === 0 && (
                <tr>
                  <td colSpan={5}>No rates registered.</td>
                </tr>
              )}
              {rates.map((r) => (
                <tr key={r.template_id}>
                  <td>{r.template_id}</td>
                  <td>{r.collection_name}</td>
                  <td>{r.schema_name}</td>
                  <td>{r.backing.quantity}</td>
                  <td>{r.enabled ? "Enabled" : "Disabled"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="admin-section">
        <h2>Drops</h2>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Drop</th>
                <th>Collection</th>
                <th>Template</th>
                <th>Price</th>
                <th>Backing</th>
                <th>Minted</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {drops.length === 0 && (
                <tr>
                  <td colSpan={7}>No drops configured.</td>
                </tr>
              )}
              {drops.map((d) => (
                <tr key={d.drop_id}>
                  <td>{d.drop_id}</td>
                  <td>{d.collection_name}</td>
                  <td>{d.template_id}</td>
                  <td>{d.price.quantity}</td>
                  <td>{d.backing.quantity}</td>
                  <td>{d.minted_count}</td>
                  <td>{d.enabled ? "Enabled" : "Paused"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {account && <ForumAdminPanel account={account} />}
    </div>
  );
}
